import { useState, useEffect } from 'react';
import { Building, Users, Bell, Palette, Save, Plus, Trash2, Mail, Phone, MapPin, Shield, Loader2, AlertTriangle } from 'lucide-react';
import { api } from '../services/api';

const defaultEmpresaData = {
  id: null,
  nome: 'Minha Empresa',
  cnpj: '',
  email: '',
  telefone: '',
  endereco: ''
};

const mapEmpresaResponse = (data) => ({
  id: data?.id ?? null,
  nome: data?.nome || data?.razao_social || defaultEmpresaData.nome,
  cnpj: data?.cnpj || '',
  email: data?.email || '',
  telefone: data?.telefone || '',
  endereco: data?.endereco || ''
});

const statusOptions = [
  { value: 'pendente', label: 'Pendentes' },
  { value: 'vencida', label: 'Vencidas' },
  { value: 'parcial', label: 'Parciais' },
  { value: 'concluida', label: 'Pagas' }
];

const statusLabelMap = statusOptions.reduce((acc, item) => {
  acc[item.value] = item.label;
  return acc;
}, {});

const createEmptyRuleForm = () => ({
  name: '',
  lancamento_tipo: 'ambos',
  antecedencia_dias: '3',
  filters: {
    status: ['vencida'],
    valorMinimo: '',
    valorMaximo: '',
    cliente: '',
    fornecedor: '',
    descricao: ''
  },
  is_active: true
});

export default function Configuracoes() {
  const [activeTab, setActiveTab] = useState('empresa');
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    senha: '',
    role: 'user'
  });

  const [empresaData, setEmpresaData] = useState(defaultEmpresaData);
  const [empresaLoading, setEmpresaLoading] = useState(false);
  const [empresaSaving, setEmpresaSaving] = useState(false);

  const [notificationRules, setNotificationRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [rulesError, setRulesError] = useState('');
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleForm, setRuleForm] = useState(createEmptyRuleForm());
  const [ruleSaving, setRuleSaving] = useState(false);

  useEffect(() => {
    if (activeTab === 'usuarios') {
      loadUsuarios();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'notificacoes' && notificationRules.length === 0) {
      loadNotificationRules();
    }
  }, [activeTab]);

  useEffect(() => {
    const carregarEmpresa = async () => {
      try {
        setEmpresaLoading(true);
        const dados = await api.getEmpresaConfig();
        if (dados) {
          setEmpresaData({ ...defaultEmpresaData, ...mapEmpresaResponse(dados) });
        }
      } catch (error) {
        console.error('Erro ao carregar dados da empresa:', error);
      } finally {
        setEmpresaLoading(false);
      }
    };

    carregarEmpresa();
  }, []);

  const loadUsuarios = async () => {
    try {
      setLoading(true);
      const data = await api.getUsuarios();
      setUsuarios(data);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadNotificationRules = async () => {
    try {
      setRulesLoading(true);
      setRulesError('');
      const response = await api.getNotificationRules();
      setNotificationRules(response?.rules || []);
    } catch (error) {
      console.error('Erro ao carregar regras de notificação:', error);
      setRulesError(error.message || 'Não foi possível carregar as regras de notificação.');
    } finally {
      setRulesLoading(false);
    }
  };

  const resetRuleForm = () => {
    setRuleForm(createEmptyRuleForm());
    setEditingRule(null);
  };

  const handleOpenCreateRule = () => {
    resetRuleForm();
    setShowRuleForm(true);
  };

  const handleEditRule = (rule) => {
    if (!rule) return;
    const nextForm = createEmptyRuleForm();
    nextForm.name = rule.name || '';
    nextForm.lancamento_tipo = rule.lancamento_tipo || 'ambos';
    nextForm.antecedencia_dias = String(rule.antecedencia_dias ?? '0');
    nextForm.filters.status = Array.isArray(rule?.filters?.status)
      ? [...rule.filters.status]
      : rule?.filters?.status
        ? [rule.filters.status]
        : [];
    nextForm.filters.valorMinimo = rule?.filters?.valorMinimo !== undefined && rule?.filters?.valorMinimo !== null
      ? String(rule.filters.valorMinimo)
      : '';
    nextForm.filters.valorMaximo = rule?.filters?.valorMaximo !== undefined && rule?.filters?.valorMaximo !== null
      ? String(rule.filters.valorMaximo)
      : '';
    nextForm.filters.cliente = rule?.filters?.cliente || '';
    nextForm.filters.fornecedor = rule?.filters?.fornecedor || '';
    nextForm.filters.descricao = rule?.filters?.descricao || '';
    nextForm.is_active = rule?.is_active !== false;

    setRuleForm(nextForm);
    setEditingRule(rule);
    setShowRuleForm(true);
  };

  const handleStatusToggle = (statusValue) => {
    setRuleForm((prev) => {
      const currentStatus = Array.isArray(prev.filters.status) ? [...prev.filters.status] : [];
      const exists = currentStatus.includes(statusValue);
      const nextStatus = exists
        ? currentStatus.filter((item) => item !== statusValue)
        : [...currentStatus, statusValue];

      return {
        ...prev,
        filters: {
          ...prev.filters,
          status: nextStatus
        }
      };
    });
  };

  const handleFilterFieldChange = (field, value) => {
    setRuleForm((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        [field]: value
      }
    }));
  };

  const handleRuleFieldChange = (field, value) => {
    setRuleForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCancelRuleForm = () => {
    resetRuleForm();
    setShowRuleForm(false);
  };

  const handleRuleSubmit = async (event) => {
    event.preventDefault();

    const trimmedName = ruleForm.name.trim();
    if (!trimmedName) {
      alert('Informe um nome para a regra de notificação.');
      return;
    }

    const selectedStatus = Array.isArray(ruleForm.filters.status)
      ? ruleForm.filters.status.filter(Boolean)
      : [];

    const filtersPayload = {};
    if (selectedStatus.length > 0) {
      filtersPayload.status = selectedStatus;
    }

    if (ruleForm.filters.valorMinimo !== '') {
      const parsedMin = Number(ruleForm.filters.valorMinimo);
      if (!Number.isNaN(parsedMin)) {
        filtersPayload.valorMinimo = parsedMin;
      }
    }

    if (ruleForm.filters.valorMaximo !== '') {
      const parsedMax = Number(ruleForm.filters.valorMaximo);
      if (!Number.isNaN(parsedMax)) {
        filtersPayload.valorMaximo = parsedMax;
      }
    }

    const cliente = ruleForm.filters.cliente.trim();
    if (cliente) {
      filtersPayload.cliente = cliente;
    }

    const fornecedor = ruleForm.filters.fornecedor.trim();
    if (fornecedor) {
      filtersPayload.fornecedor = fornecedor;
    }

    const descricao = ruleForm.filters.descricao.trim();
    if (descricao) {
      filtersPayload.descricao = descricao;
    }

    const payload = {
      name: trimmedName,
      lancamento_tipo: ruleForm.lancamento_tipo,
      antecedencia_dias: Number(ruleForm.antecedencia_dias) || 0,
      filters: filtersPayload,
      is_active: ruleForm.is_active
    };

    try {
      setRuleSaving(true);
      if (editingRule?.id) {
        const response = await api.updateNotificationRule(editingRule.id, payload);
        const updatedRule = response?.rule;
        if (updatedRule) {
          setNotificationRules((prev) => prev.map((rule) => (rule.id === editingRule.id ? updatedRule : rule)));
        }
      } else {
        const response = await api.createNotificationRule(payload);
        const createdRule = response?.rule;
        if (createdRule) {
          setNotificationRules((prev) => [createdRule, ...prev]);
        }
      }

      resetRuleForm();
      setShowRuleForm(false);
    } catch (error) {
      console.error('Erro ao salvar regra de notificação:', error);
      alert(error.message || 'Não foi possível salvar a regra de notificação.');
    } finally {
      setRuleSaving(false);
    }
  };

  const handleDeleteRule = async (rule) => {
    if (!rule?.id) return;
    const confirmed = window.confirm(`Deseja realmente excluir a regra "${rule.name}"?`);
    if (!confirmed) return;

    try {
      await api.deleteNotificationRule(rule.id);
      setNotificationRules((prev) => prev.filter((item) => item.id !== rule.id));
      if (editingRule?.id === rule.id) {
        resetRuleForm();
        setShowRuleForm(false);
      }
    } catch (error) {
      console.error('Erro ao excluir regra de notificação:', error);
      alert(error.message || 'Não foi possível excluir a regra de notificação.');
    }
  };

  const formatCurrency = (value) => {
    const number = Number(value);
    if (Number.isNaN(number)) {
      return 'R$ 0,00';
    }
    return number.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      await api.createUsuario(formData);
      setShowAddUser(false);
      setFormData({ nome: '', email: '', senha: '', role: 'user' });
      loadUsuarios();
    } catch (error) {
      console.error('Erro ao criar usuário:', error);
      alert('Erro ao criar usuário');
    }
  };

  const handleSaveEmpresa = async (e) => {
    e.preventDefault();
    try {
      setEmpresaSaving(true);
      const payload = {
        id: empresaData.id,
        nome: empresaData.nome,
        cnpj: empresaData.cnpj,
        email: empresaData.email,
        telefone: empresaData.telefone,
        endereco: empresaData.endereco
      };

      const resposta = await api.saveEmpresaConfig(payload);
      setEmpresaData({ ...defaultEmpresaData, ...mapEmpresaResponse(resposta) });
      alert('Dados da empresa salvos com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar dados da empresa:', error);
      alert('Não foi possível salvar os dados da empresa.');
    } finally {
      setEmpresaSaving(false);
    }
  };

  const tabs = [
    { id: 'empresa', label: 'Empresa', icon: Building },
    { id: 'usuarios', label: 'Usuários', icon: Users },
    { id: 'notificacoes', label: 'Notificações', icon: Bell }
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Configurações</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">Gerencie as configurações do sistema</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar de Tabs */}
        <div className="lg:col-span-1">
          <div className="card p-2 space-y-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 font-medium ${
                    activeTab === tab.id
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          <div className="card">
            {/* Empresa */}
            {activeTab === 'empresa' && (
              <form onSubmit={handleSaveEmpresa} className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                    <Building className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                    Dados da Empresa
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">
                    Configure as informações principais da sua empresa
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      <Building className="w-4 h-4 inline mr-1" />
                      Nome da Empresa *
                    </label>
                    <input
                      type="text"
                      required
                      className="input-field"
                      placeholder="Ex: Minha Empresa LTDA"
                      value={empresaData.nome}
                      onChange={(e) => setEmpresaData({ ...empresaData, nome: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      <Shield className="w-4 h-4 inline mr-1" />
                      CNPJ
                    </label>
                    <input
                      type="text"
                      className="input-field"
                      value={empresaData.cnpj}
                      onChange={(e) => setEmpresaData({ ...empresaData, cnpj: e.target.value })}
                      placeholder="00.000.000/0000-00"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      <Mail className="w-4 h-4 inline mr-1" />
                      Email
                    </label>
                    <input
                      type="email"
                      className="input-field"
                      placeholder="contato@empresa.com"
                      value={empresaData.email}
                      onChange={(e) => setEmpresaData({ ...empresaData, email: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      <Phone className="w-4 h-4 inline mr-1" />
                      Telefone
                    </label>
                    <input
                      type="tel"
                      className="input-field"
                      placeholder="(00) 00000-0000"
                      value={empresaData.telefone}
                      onChange={(e) => setEmpresaData({ ...empresaData, telefone: e.target.value })}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      <MapPin className="w-4 h-4 inline mr-1" />
                      Endereço
                    </label>
                    <textarea
                      rows="3"
                      className="input-field"
                      placeholder="Rua, número, bairro, cidade - UF"
                      value={empresaData.endereco}
                      onChange={(e) => setEmpresaData({ ...empresaData, endereco: e.target.value })}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={empresaLoading || empresaSaving}
                  className={`btn-primary flex items-center gap-2 ${
                    empresaLoading || empresaSaving ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  <Save className="w-5 h-5" />
                  {empresaSaving ? 'Salvando...' : 'Salvar Alterações'}
                </button>
              </form>
            )}

            {/* Usuários */}
            {activeTab === 'usuarios' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                      <Users className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                      Usuários do Sistema
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
                      Gerencie os usuários que têm acesso ao sistema
                    </p>
                  </div>
                  <button
                    onClick={() => setShowAddUser(true)}
                    className="btn-primary flex items-center gap-2"
                  >
                    <Plus className="w-5 h-5" />
                    Adicionar
                  </button>
                </div>

                {loading ? (
                  <div className="text-center py-12">
                    <div className="spinner h-8 w-8 mx-auto"></div>
                  </div>
                ) : usuarios.length === 0 ? (
                  <div className="text-center py-12 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-xl">
                    <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-400">Nenhum usuário cadastrado</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {usuarios.map((usuario) => (
                      <div 
                        key={usuario.id} 
                        className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-lg">
                            <span className="text-white font-bold text-lg">
                              {usuario.nome.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900 dark:text-white">{usuario.nome}</p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{usuario.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`badge ${
                            usuario.role === 'admin' ? 'badge-info' : 'badge-gray'
                          }`}>
                            {usuario.role === 'admin' ? 'Administrador' : 'Usuário'}
                          </span>
                          {usuario.id !== 1 && (
                            <button className="p-2 text-danger-600 dark:text-danger-400 hover:bg-danger-50 dark:hover:bg-danger-900/20 rounded-lg transition-colors">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Modal Add User */}
                {showAddUser && (
                  <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full p-6 shadow-2xl animate-slide-in">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Adicionar Usuário</h3>
                      <form onSubmit={handleAddUser} className="space-y-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Nome Completo *
                          </label>
                          <input
                            type="text"
                            required
                            className="input-field"
                            placeholder="João Silva"
                            value={formData.nome}
                            onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Email *
                          </label>
                          <input
                            type="email"
                            required
                            className="input-field"
                            placeholder="joao@email.com"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Senha *
                          </label>
                          <input
                            type="password"
                            required
                            className="input-field"
                            placeholder="••••••••"
                            value={formData.senha}
                            onChange={(e) => setFormData({ ...formData, senha: e.target.value })}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Tipo de Usuário *
                          </label>
                          <select
                            required
                            className="input-field"
                            value={formData.role}
                            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                          >
                            <option value="user">Usuário</option>
                            <option value="admin">Administrador</option>
                          </select>
                        </div>

                        <div className="flex gap-3 pt-4">
                          <button type="submit" className="flex-1 btn-primary">
                            Criar Usuário
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowAddUser(false);
                              setFormData({ nome: '', email: '', senha: '', role: 'user' });
                            }}
                            className="flex-1 btn-secondary"
                          >
                            Cancelar
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Notificações */}
            {activeTab === 'notificacoes' && (
              <div className="space-y-6">
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                      <Bell className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                      Notificações personalizadas
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 text-sm">
                      Crie regras para receber alertas automáticos de vencimentos e outros eventos críticos.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleOpenCreateRule}
                    className="btn-primary flex items-center justify-center gap-2 self-start"
                  >
                    <Plus className="w-5 h-5" />
                    Nova regra
                  </button>
                </div>

                {rulesError && (
                  <div className="p-4 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg">
                    <div className="flex items-center gap-2 text-danger-600 dark:text-danger-300">
                      <AlertTriangle className="w-5 h-5" />
                      <p className="text-sm">{rulesError}</p>
                    </div>
                  </div>
                )}

                {showRuleForm && (
                  <div className="p-6 bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-xl space-y-5">
                    <form onSubmit={handleRuleSubmit} className="space-y-5">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Nome da regra *
                          </label>
                          <input
                            type="text"
                            required
                            className="input-field"
                            placeholder="Ex: Alertar contas a pagar em 3 dias"
                            value={ruleForm.name}
                            onChange={(e) => handleRuleFieldChange('name', e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Tipo de lançamento
                          </label>
                          <select
                            className="input-field"
                            value={ruleForm.lancamento_tipo}
                            onChange={(e) => handleRuleFieldChange('lancamento_tipo', e.target.value)}
                          >
                            <option value="ambos">Todos os lançamentos</option>
                            <option value="pagar">Somente contas a pagar</option>
                            <option value="receber">Somente contas a receber</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Antecedência (dias)
                          </label>
                          <input
                            type="number"
                            min="0"
                            className="input-field"
                            value={ruleForm.antecedencia_dias}
                            onChange={(e) => handleRuleFieldChange('antecedencia_dias', e.target.value)}
                          />
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            O sistema enviará notificações para vencimentos dentro deste período.
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Status monitorados
                          </label>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {statusOptions.map((option) => {
                              const checked = Array.isArray(ruleForm.filters.status)
                                ? ruleForm.filters.status.includes(option.value)
                                : false;
                              return (
                                <label
                                  key={option.value}
                                  className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all cursor-pointer ${
                                    checked
                                      ? 'bg-primary-50 dark:bg-primary-900/40 border-primary-200 dark:border-primary-700 text-primary-700 dark:text-primary-300'
                                      : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-primary-300'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    className="sr-only"
                                    checked={checked}
                                    onChange={() => handleStatusToggle(option.value)}
                                  />
                                  {option.label}
                                </label>
                              );
                            })}
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                            Se nenhum status for selecionado, a regra considerará todos os lançamentos no período informado.
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Valor mínimo (R$)
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="input-field"
                            value={ruleForm.filters.valorMinimo}
                            onChange={(e) => handleFilterFieldChange('valorMinimo', e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Valor máximo (R$)
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="input-field"
                            value={ruleForm.filters.valorMaximo}
                            onChange={(e) => handleFilterFieldChange('valorMaximo', e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Cliente contém
                          </label>
                          <input
                            type="text"
                            className="input-field"
                            value={ruleForm.filters.cliente}
                            onChange={(e) => handleFilterFieldChange('cliente', e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Fornecedor contém
                          </label>
                          <input
                            type="text"
                            className="input-field"
                            value={ruleForm.filters.fornecedor}
                            onChange={(e) => handleFilterFieldChange('fornecedor', e.target.value)}
                          />
                        </div>
                        <div className="md:col-span-3">
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Descrição contém
                          </label>
                          <input
                            type="text"
                            className="input-field"
                            value={ruleForm.filters.descricao}
                            onChange={(e) => handleFilterFieldChange('descricao', e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">Regra ativa</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Desative temporariamente sem apagar sua configuração.
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={ruleForm.is_active}
                            onChange={(e) => handleRuleFieldChange('is_active', e.target.checked)}
                          />
                          <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                        </label>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-3 justify-end">
                        <button
                          type="submit"
                          disabled={ruleSaving}
                          className="btn-primary flex items-center justify-center gap-2 w-full sm:w-auto"
                        >
                          {ruleSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                          {editingRule ? 'Atualizar regra' : 'Salvar regra'}
                        </button>
                        <button
                          type="button"
                          onClick={handleCancelRuleForm}
                          disabled={ruleSaving}
                          className="btn-secondary w-full sm:w-auto"
                        >
                          Cancelar
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                <div className="space-y-4">
                  {rulesLoading ? (
                    <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Carregando regras...</span>
                    </div>
                  ) : notificationRules.length === 0 ? (
                    <div className="p-6 border border-dashed border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900/40 text-center space-y-3">
                      <p className="text-gray-600 dark:text-gray-400 text-sm">
                        Nenhuma regra configurada ainda. Crie sua primeira regra para ser avisado sobre vencimentos importantes.
                      </p>
                      <button
                        type="button"
                        onClick={handleOpenCreateRule}
                        className="btn-primary inline-flex items-center gap-2 justify-center"
                      >
                        <Plus className="w-4 h-4" />
                        Criar primeira regra
                      </button>
                    </div>
                  ) : (
                    notificationRules.map((rule) => {
                      const statusList = Array.isArray(rule?.filters?.status) ? rule.filters.status : [];
                      const otherFilters = { ...(rule?.filters || {}) };
                      delete otherFilters.status;

                      return (
                        <div
                          key={rule.id}
                          className="p-5 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900/40 shadow-sm"
                        >
                          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                            <div className="space-y-2">
                              <div className="flex items-center gap-3">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{rule.name}</h3>
                                <span
                                  className={`px-2.5 py-1 text-xs font-semibold rounded-full ${
                                    rule.is_active
                                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                      : 'bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                                  }`}
                                >
                                  {rule.is_active ? 'Ativa' : 'Inativa'}
                                </span>
                              </div>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                <span className="font-medium text-gray-700 dark:text-gray-300">Tipo:</span>{' '}
                                {rule.lancamento_tipo === 'ambos'
                                  ? 'Todos os lançamentos'
                                  : rule.lancamento_tipo === 'pagar'
                                    ? 'Contas a pagar'
                                    : 'Contas a receber'}
                                {' · '}
                                <span className="font-medium text-gray-700 dark:text-gray-300">Antecedência:</span> {rule.antecedencia_dias} dia(s)
                              </p>
                              {statusList.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                  {statusList.map((status) => (
                                    <span
                                      key={status}
                                      className="px-2 py-1 rounded-full text-xs font-medium bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300"
                                    >
                                      {statusLabelMap[status] || status}
                                    </span>
                                  ))}
                                </div>
                              )}
                              {Object.keys(otherFilters).length > 0 && (
                                <div className="flex flex-wrap gap-2 text-xs text-gray-600 dark:text-gray-400">
                                  {otherFilters.valorMinimo !== undefined && otherFilters.valorMinimo !== null && otherFilters.valorMinimo !== '' && (
                                    <span className="px-2 py-1 rounded bg-gray-100 dark:bg-gray-800/60">
                                      Valor ≥ {formatCurrency(otherFilters.valorMinimo)}
                                    </span>
                                  )}
                                  {otherFilters.valorMaximo !== undefined && otherFilters.valorMaximo !== null && otherFilters.valorMaximo !== '' && (
                                    <span className="px-2 py-1 rounded bg-gray-100 dark:bg-gray-800/60">
                                      Valor ≤ {formatCurrency(otherFilters.valorMaximo)}
                                    </span>
                                  )}
                                  {otherFilters.cliente && (
                                    <span className="px-2 py-1 rounded bg-gray-100 dark:bg-gray-800/60">
                                      Cliente: {otherFilters.cliente}
                                    </span>
                                  )}
                                  {otherFilters.fornecedor && (
                                    <span className="px-2 py-1 rounded bg-gray-100 dark:bg-gray-800/60">
                                      Fornecedor: {otherFilters.fornecedor}
                                    </span>
                                  )}
                                  {otherFilters.descricao && (
                                    <span className="px-2 py-1 rounded bg-gray-100 dark:bg-gray-800/60">
                                      Descrição contém: {otherFilters.descricao}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                className="btn-secondary"
                                onClick={() => handleEditRule(rule)}
                              >
                                Editar
                              </button>
                              <button
                                type="button"
                                className="btn-secondary text-danger-600 dark:text-danger-400 border-danger-200 dark:border-danger-700"
                                onClick={() => handleDeleteRule(rule)}
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Excluir
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}